/**
 * Environment Configuration
 *
 * Centralized access to environment variables with type safety
 */

// Auth is handled by BFF (HttpOnly cookies) — no Cognito config needed in frontend.
// VITE_API_URL: set in .env or CI. Production: https://portal.ramps.moneygram.com
// Local dev: VITE_API_URL=http://localhost:3001

export const env = {
	// Sentry Configuration
	SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
	SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",

	// Environment checks
	isDevelopment: import.meta.env.DEV,
	isProduction: import.meta.env.PROD,

	// App version for release tracking
	APP_VERSION: import.meta.env.VITE_APP_VERSION || "development",

	// API Configuration (always API mode — localStorage mode removed)
	// In dev mode, use empty string so requests go through Vite proxy (/api/* → BFF)
	API_URL:
		import.meta.env.VITE_API_URL ||
		(import.meta.env.DEV ? "" : "https://portal.ramps.moneygram.com"),

	// AI Assistant Widget (OpenCX)
	WIDGET_TOKEN: import.meta.env.VITE_WIDGET_TOKEN || "",

	// AWS Cognito Configuration
	COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID || "",
	COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
	AWS_REGION: import.meta.env.VITE_AWS_REGION || "us-east-1",
} as const;

/**
 * Log which env vars were explicitly set vs using defaults.
 * Call this at app startup (e.g. from main.tsx) to verify deployment
 * configuration.
 *
 * Disabled in production builds — partners shouldn't see API URLs, Cognito
 * IDs, and feature-flag values in their browser console. Vite's MODE is
 * baked in at build time, so this is a true compile-out: the production
 * bundle ships an empty function body and the strings below get
 * tree-shaken away.
 */
export function logEnvConfig(): void {
	if (import.meta.env.PROD) return;
	const checks: {
		name: string;
		envKey: string;
		rawValue: string | undefined;
		resolvedValue: unknown;
		fallback: string;
	}[] = [
		{
			name: "API_URL",
			envKey: "VITE_API_URL",
			rawValue: import.meta.env.VITE_API_URL,
			resolvedValue: env.API_URL,
			fallback: "https://portal.ramps.moneygram.com",
		},
		{
			name: "COGNITO_USER_POOL_ID",
			envKey: "VITE_COGNITO_USER_POOL_ID",
			rawValue: import.meta.env.VITE_COGNITO_USER_POOL_ID,
			resolvedValue: env.COGNITO_USER_POOL_ID,
			fallback: '""',
		},
		{
			name: "COGNITO_CLIENT_ID",
			envKey: "VITE_COGNITO_CLIENT_ID",
			rawValue: import.meta.env.VITE_COGNITO_CLIENT_ID,
			resolvedValue: env.COGNITO_CLIENT_ID,
			fallback: '""',
		},
		{
			name: "AWS_REGION",
			envKey: "VITE_AWS_REGION",
			rawValue: import.meta.env.VITE_AWS_REGION,
			resolvedValue: env.AWS_REGION,
			fallback: "us-east-1",
		},
		{
			name: "SENTRY_DSN",
			envKey: "VITE_SENTRY_DSN",
			rawValue: import.meta.env.VITE_SENTRY_DSN,
			resolvedValue: env.SENTRY_DSN,
			fallback: "(unset)",
		},
		{
			name: "SENTRY_ENVIRONMENT",
			envKey: "VITE_SENTRY_ENVIRONMENT",
			rawValue: import.meta.env.VITE_SENTRY_ENVIRONMENT,
			resolvedValue: env.SENTRY_ENVIRONMENT,
			fallback: "development",
		},
		{
			name: "APP_VERSION",
			envKey: "VITE_APP_VERSION",
			rawValue: import.meta.env.VITE_APP_VERSION,
			resolvedValue: env.APP_VERSION,
			fallback: "development",
		},
		{
			name: "WIDGET_TOKEN",
			envKey: "VITE_WIDGET_TOKEN",
			rawValue: import.meta.env.VITE_WIDGET_TOKEN,
			resolvedValue: env.WIDGET_TOKEN ? "(set)" : "(empty)",
			fallback: "(empty — AI Assistant disabled)",
		},
	];

	const setVars = checks.filter(
		(c) => c.rawValue !== undefined && c.rawValue !== "",
	);
	const defaultVars = checks.filter(
		(c) => c.rawValue === undefined || c.rawValue === "",
	);

	console.group(
		`%c[Env Config] Ramps Portal UI — Runtime Environment (${import.meta.env.MODE})`,
		"font-weight:bold; color:#4f9eff",
	);

	if (setVars.length > 0) {
		console.group(
			`%c[SET] ${setVars.length} variable(s) explicitly configured`,
			"color:#22c55e",
		);
		for (const v of setVars) {
			console.log(
				`  ${v.envKey} = %c${String(v.resolvedValue)}`,
				"color:#22c55e; font-weight:bold",
			);
		}
		console.groupEnd();
	}

	if (defaultVars.length > 0) {
		console.group(
			`%c[DEFAULT] ${defaultVars.length} variable(s) using fallback values`,
			"color:#f59e0b",
		);
		for (const v of defaultVars) {
			console.log(
				`  ${v.envKey} %c(not set)%c -> fallback: %c${v.fallback}`,
				"color:#ef4444",
				"color:inherit",
				"color:#f59e0b; font-weight:bold",
			);
		}
		console.groupEnd();
	}

	// Check localStorage availability (used for filters, etc. — auth uses HttpOnly cookies)
	try {
		const testKey = "__ramps_portal_storage_test__";
		localStorage.setItem(testKey, "1");
		localStorage.removeItem(testKey);
	} catch (e) {
		console.warn(
			"[Env Config] localStorage is not available. Some features may not persist.",
			e,
		);
	}

	console.log("Resolved config:", { ...env });
	console.groupEnd();
}
